import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, CommandLineIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import useUiStore from '../store/uiStore';
import { coreAPI, farmAPI } from '../services/api';
import { flattenNavigation, navigationConfig, sectionShortcuts } from '../navigation/navConfig';
import useAuthStore from '../store/authStore';
import { filterAccessibleItems } from '../utils/permissions';

const quickEntries = [
  { name: 'جست‌وجوی سریع دام‌ها', href: '/cows', keywords: 'دام cows search herd ear tag', type: 'page', section: 'صفحه‌ها' },
  { name: 'فهرست‌های ذخیره‌شده', href: '/lists/saved?tab=saved', keywords: 'lists saved templates builder', type: 'page', section: 'صفحه‌ها' },
  { name: 'برنامه روزانه', href: '/daily-plan', keywords: 'ops daily planning dashboard', type: 'page', section: 'صفحه‌ها' },
  { name: 'کارت دام', href: '/cows/individual', keywords: 'cow card individual', type: 'page', section: 'صفحه‌ها' },
  { name: 'میز سلامت و درمان', href: '/health/workbench', keywords: 'health treatment workbench hospital queue', type: 'page', section: 'صفحه‌ها', permission: 'can_create_treatment' },
  { name: 'مرکز KPI', href: '/reports/kpi-center', keywords: 'kpi center reports executive', type: 'page', section: 'صفحه‌ها', permission: 'can_view_management_reports' },
  { name: 'آزمایشگاه تحلیل', href: '/reports/analytics-lab', keywords: 'analytics lab pivot drilldown self service reports', type: 'page', section: 'صفحه‌ها', permission: 'analytics_lab.view' },
  { name: 'تحلیل چرخه باروری', href: '/reports/pregnancy-cycles', keywords: 'pregnancy cycle 21 day pregnancy rate conception submission fertility آبستنی تلقیح گیرایی', type: 'page', section: 'صفحه‌ها', permission: 'pregnancy_cycles.view' },
  { name: 'مرکز کیفیت داده', href: '/data-quality', keywords: 'data quality integrity audit errors analytics', type: 'page', section: 'صفحه‌ها', permission: 'data_quality.view' },
  { name: 'میز اقتصادی', href: '/economic/workbench', keywords: 'economic workbench cull monitor keep', type: 'page', section: 'صفحه‌ها', permission: 'can_manage_financial_data' },
];

function normalizeNavigationItems(user) {
  const navLeaves = filterAccessibleItems(flattenNavigation(navigationConfig), user).map((item) => ({
    name: item.name,
    href: item.href,
    section: item.trail?.[0]?.name || 'صفحه‌ها',
    keywords: item.trail?.map((entry) => entry.name).join(' ') || '',
    type: 'page',
  }));

  return filterAccessibleItems([...sectionShortcuts, ...quickEntries, ...navLeaves], user)
    .filter((item) => item.href)
    .reduce((acc, item) => {
      if (acc.some((existing) => existing.href === item.href && existing.name === item.name)) return acc;
      acc.push(item);
      return acc;
    }, []);
}

function normalizeSearchResults({ pages = [], cows = [], savedLists = [], templates = [] }) {
  return [
    ...pages.map((item) => ({ ...item, resultType: 'page', badge: item.section || 'صفحه' })),
    ...cows.map((item) => ({
      id: item.id,
      name: item.ear_tag || item.label || 'دام بدون پلاک',
      description: [item.name, item.baharband_code, item.status_display].filter(Boolean).join(' • '),
      href: `/cows/${item.id}/individual`,
      resultType: 'cow',
      section: 'دام‌ها',
      badge: 'دام',
    })),
    ...savedLists.map((item) => ({
      id: item.id,
      name: item.name,
      description: [item.scope_display || item.scope, item.is_shared ? 'اشتراکی' : 'شخصی'].filter(Boolean).join(' • '),
      href: `/lists/saved?tab=saved&saved=${item.id}&autorun=1`,
      resultType: 'saved-list',
      section: 'فهرست‌ها',
      badge: 'فهرست ذخیره‌شده',
    })),
    ...templates.map((item) => ({
      id: item.key,
      name: item.name,
      description: [item.scope_display || item.scope, item.description].filter(Boolean).join(' • '),
      href: `/lists/saved?tab=templates&template=${item.key}&autorun=1`,
      resultType: 'template',
      section: 'فهرست‌ها',
      badge: 'قالب فهرست',
    })),
  ];
}

function GlobalSearchLauncher() {
  const navigate = useNavigate();
  const globalSearchOpen = useUiStore((state) => state.globalSearchOpen);
  const setGlobalSearchOpen = useUiStore((state) => state.setGlobalSearchOpen);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [remoteResults, setRemoteResults] = useState({ cows: [], savedLists: [], templates: [] });
  const requestSeqRef = useRef(0);
  const templatesCacheRef = useRef([]);
  const user = useAuthStore((state) => state.user);
  const searchablePages = useMemo(() => normalizeNavigationItems(user), [user]);

  useEffect(() => {
    let gPressed = false;
    const timerRef = { current: null };
    const handleKeyDown = (event) => {
      const targetTag = event.target?.tagName;
      const isInputLike = ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTag) || event.target?.isContentEditable;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setGlobalSearchOpen(true);
        return;
      }
      if (!isInputLike && event.key === '/') {
        event.preventDefault();
        setGlobalSearchOpen(true);
        return;
      }
      if (!isInputLike && !globalSearchOpen && event.key.toLowerCase() === 'g') {
        gPressed = true;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { gPressed = false; }, 1000);
        return;
      }
      if (!isInputLike && !globalSearchOpen && gPressed) {
        const key = event.key.toLowerCase();
        const map = { c: '/cows', r: '/repro', h: '/health/workbench', o: '/ops', e: '/economic/workbench' };
        if (map[key]) {
          event.preventDefault();
          gPressed = false;
          navigate(map[key]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [globalSearchOpen, navigate, setGlobalSearchOpen]);

  useEffect(() => {
    if (!globalSearchOpen) {
      setQuery('');
      setHighlightedIndex(0);
      setRemoteResults({ cows: [], savedLists: [], templates: [] });
    }
  }, [globalSearchOpen]);

  const localPageResults = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return searchablePages.slice(0, 10);
    return searchablePages.filter((item) => (`${item.name} ${item.section || ''} ${item.keywords || ''}`).toLowerCase().includes(needle)).slice(0, 8);
  }, [query, searchablePages]);

  useEffect(() => {
    if (!globalSearchOpen) return undefined;
    const needle = query.trim();
    if (needle.length < 2) {
      setLoading(false);
      setRemoteResults({ cows: [], savedLists: [], templates: [] });
      return undefined;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);
    const handler = setTimeout(async () => {
      try {
        const promises = [
          farmAPI.getCowsSelectList({ search: needle, page_size: 6 }),
          coreAPI.getSavedLists({ search: needle, page_size: 6, ordering: 'name' }),
        ];

        if (!templatesCacheRef.current.length) {
          promises.push(coreAPI.getSavedListTemplates());
        }

        const [cowsResp, savedListsResp, templatesResp] = await Promise.all(promises);
        if (seq !== requestSeqRef.current) return;
        if (templatesResp?.data?.results) {
          templatesCacheRef.current = templatesResp.data.results;
        }

        const templateNeedle = needle.toLowerCase();
        const matchingTemplates = (templatesCacheRef.current || [])
          .filter((item) => `${item.name || ''} ${item.key || ''} ${item.description || ''}`.toLowerCase().includes(templateNeedle))
          .slice(0, 5);

        setRemoteResults({
          cows: Array.isArray(cowsResp?.data) ? cowsResp.data : (cowsResp?.data?.results || []),
          savedLists: savedListsResp?.data?.results || savedListsResp?.data || [],
          templates: matchingTemplates,
        });
      } catch (error) {
        if (seq !== requestSeqRef.current) return;
        setRemoteResults({ cows: [], savedLists: [], templates: [] });
      } finally {
        if (seq === requestSeqRef.current) {
          setLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(handler);
  }, [globalSearchOpen, query]);

  const results = useMemo(() => {
    return normalizeSearchResults({
      pages: localPageResults,
      cows: remoteResults.cows,
      savedLists: remoteResults.savedLists,
      templates: remoteResults.templates,
    }).slice(0, 18);
  }, [localPageResults, remoteResults]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  const openItem = (item) => {
    setGlobalSearchOpen(false);
    navigate(item.href);
  };

  const onInputKeyDown = (event) => {
    if (!results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      openItem(results[highlightedIndex] || results[0]);
    }
  };

  return (
    <Modal isOpen={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} title="جست‌وجوی سراسری / Command Palette" size="lg">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            className="w-full bg-transparent text-sm outline-none"
            placeholder="صفحه، دام، پلاک، فهرست ذخیره‌شده یا قالب فهرست را جست‌وجو کن..."
          />
          <span className="hidden items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-500 sm:inline-flex">
            <CommandLineIcon className="h-3.5 w-3.5" />
            Ctrl/Cmd + K
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">صفحه‌ها</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">دام‌ها</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">فهرست‌های ذخیره‌شده</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">قالب‌های فهرست</span>
        </div>

        <div className="grid gap-2">
          {results.map((item, index) => (
            <button
              key={`${item.resultType || item.type}-${item.href}-${item.id || item.name}`}
              type="button"
              onClick={() => openItem(item)}
              className={`flex items-start justify-between rounded-2xl border px-4 py-3 text-right transition ${index === highlightedIndex ? 'border-primary-200 bg-primary-50' : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-primary-50'}`}
            >
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-semibold text-slate-900">{item.name}</div>
                  <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-500">{item.badge}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{item.description || item.section || 'میان‌بر'}</div>
              </div>
              <div className="text-[11px] text-slate-400 ltr">{item.href}</div>
            </button>
          ))}
          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
              در حال جست‌وجوی دام‌ها و فهرست‌ها...
            </div>
          ) : null}
          {!loading && !results.length ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              نتیجه‌ای برای این عبارت پیدا نشد.
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export default GlobalSearchLauncher;
